/**
 * NpcUnitModule — the NPC CREATOR's runtime host (Edit Mode → NPC).
 *
 * An `npc_unit` row is a blank-slate character: a body (one of the scanned
 * traveler GLBs), a spawn transform, and Lua SOURCE. This module owns the
 * PRESENTATION and the SENSES of every unit in the current world — bodies,
 * nameplates + state line, speech bubbles, ground-following walks, gestures,
 * the proximity/eye-contact detectors, the conversation window (greeting,
 * topics, choices, free text), per-player memory, and the versona lookups —
 * and exposes them as the `npc.*` Lua table the WorldScriptRuntime hands to
 * each unit's own script slot. Scripts are EXPERIENTIAL: every visitor's
 * client runs them against ITS player (the world-script law), so "user
 * detection" means the local player; only writes that must be shared
 * (asking a versona) go through a reducer, and only from a host client.
 *
 * Deliberately detached from the versona architecture: no agent, no memory
 * table, no brain worker — an NPC knows exactly what its script reads.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { groundYIn } from '../lib/grounding.js';
import { prefabAssetId } from '../lib/player_models.js';
import { capsIn } from '../lib/world_caps.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { nameFromTables } from '../lib/versona_names.js';
import { markShared, deferDisposeTree } from '../lib/defer_dispose.js';
import { biomeAt, terrainHeight } from '@shared/terrain.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { settlementSites, type SettlementSite } from '../../../shared/settlements.js';
import { resolvedCityName } from '../lib/city_names.js';
import { knownCalls } from '../lib/ide/lua_api.js';
import type { AssetModule } from './AssetModule.js';
import type { DeviceModule } from './DeviceModule.js';
import { findPath, parseDialogue, parseSchedule, scheduleEntryFor, visibleChoices, parseAction, dialogueNode, type DialogueTree, type DialogueNode, type ScheduleEntry } from '../lib/devices.js';

let _capsuleGeo: THREE.CapsuleGeometry | null = null;
let _capsuleMat: THREE.MeshStandardMaterial | null = null;
function capsuleGeo(): THREE.CapsuleGeometry {
  if (!_capsuleGeo) { _capsuleGeo = new THREE.CapsuleGeometry(0.32, 1.1, 6, 12); _capsuleGeo.userData.shared = true; }
  return _capsuleGeo;
}
function capsuleMat(): THREE.MeshStandardMaterial {
  if (!_capsuleMat) { _capsuleMat = new THREE.MeshStandardMaterial({ color: 0x9fb4cc, roughness: 0.7 }); _capsuleMat.userData.shared = true; }
  return _capsuleMat;
}

export interface NpcUnitRow {
  npcId: bigint; worldId: bigint; owner: { toHexString(): string }; name: string; modelKey: string;
  posX: number; posY: number; posZ: number; yaw: number; scale: number;
  scriptSrc: string; enabled: boolean; configJson: string; updatedAt: { microsSinceUnixEpoch: bigint };
}
export interface NpcEvent { hook: string; args: unknown[] }
interface Topic { k: string; l: string; r?: string }
interface VersonaLite { id: string; name: string; x: number; z: number; state: string; job: string; mood: number }

const INTERACT_M = 3.5;
const TALK_OPEN_M = 6;
const DEFAULT_SENSE_M = 12;
const DEFAULT_SPEED = 1.6;
const MAX_TOPICS = 16;
const MAX_MEMORY_KEYS = 32;
const EMOTE_S = 1.2;
const BUBBLE_MAX = 200;
const EVENT_CAP = 64;
const MARKER_COLOR = 0xe07bff;

/** Deterministic per-NPC LCG (same rolls on every client for the same call order). */
function lcg(seed: number): () => number {
  let s = (seed >>> 0) || 1;
  return () => { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 4294967296; };
}

function wrapText(text: string, width: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    if ((cur + ' ' + w).trim().length > width && cur) { lines.push(cur); cur = w; } else cur = (cur + ' ' + w).trim();
    if (lines.length >= 4) break;
  }
  if (cur && lines.length < 4) lines.push(cur);
  return lines;
}

/** A canvas-backed text sprite (nameplate / speech bubble). Owned textures — disposed by the unit. */
function makeTextSprite(): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 512; canvas.height = 128;
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = markMrtOverlay(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false }));
  const s = new THREE.Sprite(mat);
  s.renderOrder = 998;
  s.userData.canvas = canvas;
  s.visible = false;
  return s;
}

function paintSprite(s: THREE.Sprite, lines: string[], opts: { bg: string; fg: string; sub?: string; font?: number; heightM: number }): void {
  const canvas = s.userData.canvas as HTMLCanvasElement;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const font = opts.font ?? 34;
  const lineH = font * 1.25;
  const h = Math.ceil(lineH * lines.length + 28);
  canvas.height = Math.max(64, h);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.font = `600 ${font}px system-ui, sans-serif`;
  let w = 0;
  for (const l of lines) w = Math.max(w, ctx.measureText(l).width);
  const bw = Math.min(canvas.width, w + 40);
  const x0 = (canvas.width - bw) / 2;
  ctx.fillStyle = opts.bg;
  ctx.beginPath();
  const r = 16;
  ctx.moveTo(x0 + r, 0); ctx.lineTo(x0 + bw - r, 0); ctx.quadraticCurveTo(x0 + bw, 0, x0 + bw, r);
  ctx.lineTo(x0 + bw, canvas.height - r); ctx.quadraticCurveTo(x0 + bw, canvas.height, x0 + bw - r, canvas.height);
  ctx.lineTo(x0 + r, canvas.height); ctx.quadraticCurveTo(x0, canvas.height, x0, canvas.height - r);
  ctx.lineTo(x0, r); ctx.quadraticCurveTo(x0, 0, x0 + r, 0); ctx.fill();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  lines.forEach((l, i) => {
    ctx.fillStyle = i === 0 || !opts.sub ? opts.fg : opts.sub;
    ctx.font = `${i === 0 ? 700 : 500} ${i === 0 ? font : font * 0.82}px system-ui, sans-serif`;
    ctx.fillText(l, canvas.width / 2, 14 + i * lineH);
  });
  const tex = s.material.map as THREE.CanvasTexture;
  tex.needsUpdate = true;
  s.scale.set(opts.heightM * (canvas.width / canvas.height), opts.heightM, 1);
  s.visible = lines.length > 0 && lines.some((l) => l.length > 0);
}

/** One placed NPC: presentation + senses + the per-unit API the Lua bridge calls. */
export class NpcUnit {
  readonly id: string;
  row: NpcUnitRow;
  readonly group = new THREE.Group();
  private capsule: THREE.Mesh;
  private body: THREE.Object3D | null = null;
  private bodyKey = '';
  private assetId: bigint | null = null;
  private plate = makeTextSprite();
  private bubble = makeTextSprite();
  private bubbleUntil = 0;
  // runtime pose
  x: number; y: number; z: number; yaw: number;
  private target: { x: number; z: number } | null = null;
  private speedMps = DEFAULT_SPEED;
  private stateLabel = 'idle';
  private displayName: string;
  private hidden = false;
  private tintHex: string | null = null;
  private scaleMul = 1;
  private emoteKind: string | null = null;
  private emoteT0 = 0;
  // senses
  senseR = DEFAULT_SENSE_M;
  private playerInside = false;
  talkable = true;
  greetingText = '';
  promptText = 'Ask about anything…';
  topics: Topic[] = [];
  choices: string[] | null = null;
  private plateKey = '';
  readonly events: NpcEvent[] = [];
  private timers = new Map<string, number>();
  private rng: () => number;
  private config: Record<string, unknown> = {};
  readonly versonaLast = new Map<string, string>();
  readonly askedVersonas = new Set<string>();
  private bornAt = performance.now();
  private lastHour = -1;
  /** What the script DID lately (say/state/walk/emote/topics/asks) — the IDE's test report reads it. */
  readonly recent: string[] = [];
  // pathing (grid A* around parts/devices), path following, data-driven dialogue + schedules
  private waypoints: Array<{ x: number; z: number }> = [];
  private finalTarget: { x: number; z: number } | null = null;
  pathing = true;
  private followName: string | null = null;
  private followIdx = 0;
  tree: DialogueTree | null = null;
  node: DialogueNode | null = null;
  private schedule: ScheduleEntry[] = [];
  private scheduleHour = -1;

  constructor(row: NpcUnitRow, private readonly host: NpcUnitModule) {
    this.id = row.npcId.toString();
    this.row = row;
    this.displayName = row.name;
    this.x = row.posX; this.y = row.posY; this.z = row.posZ; this.yaw = row.yaw;
    this.rng = lcg(Number(row.npcId % 2147483647n) * 2654435761);
    this.group.userData.npcId = this.id;
    // One shared capsule geometry + material for every unit (each unit used to
    // mint its own identical pair = a new pipeline variant per NPC); stamped
    // shared so dispose()'s deferDisposeTree never frees the common pair.
    this.capsule = new THREE.Mesh(capsuleGeo(), capsuleMat());
    this.capsule.position.y = 0.9;
    this.group.add(this.capsule, this.plate, this.bubble);
    this.plate.position.y = 2.25;
    this.bubble.position.y = 2.85;
    this.parseConfig();
    this.loadFromConfig();
    this.applyRow(row, true);
  }

  /** Data the editor authored without code: a dialogue tree + a day schedule in the config JSON. */
  private loadFromConfig(): void {
    this.tree = parseDialogue(this.config.dialogue);
    this.schedule = parseSchedule(this.config.schedule);
    this.scheduleHour = -1;
  }

  // ── row sync ───────────────────────────────────────────────────────────────

  private parseConfig(): void {
    try { const c = JSON.parse(this.row.configJson || '{}') as unknown; this.config = c && typeof c === 'object' ? c as Record<string, unknown> : {}; } catch { this.config = {}; }
  }

  /** Re-apply a replicated row: a moved/renamed/re-bodied NPC snaps to its new spawn. */
  applyRow(row: NpcUnitRow, snap: boolean): void {
    const moved = row.posX !== this.row.posX || row.posY !== this.row.posY || row.posZ !== this.row.posZ || row.yaw !== this.row.yaw;
    if (row.name !== this.row.name) this.displayName = row.name;
    const cfgChanged = row.configJson !== this.row.configJson;
    this.row = row;
    this.parseConfig();
    if (cfgChanged) this.loadFromConfig();
    if (snap || moved) { this.x = row.posX; this.z = row.posZ; this.yaw = row.yaw; this.target = null; this.waypoints = []; this.finalTarget = null; }
    this.group.scale.setScalar(row.scale * this.scaleMul);
    if (row.modelKey !== this.bodyKey) this.loadBody(row.modelKey);
    this.refreshPlate();
  }

  private loadBody(key: string): void {
    this.bodyKey = key;
    const conn = atlas.conn;
    const assets = this.host.assets;
    if (!conn || !assets) return;
    const id = prefabAssetId(conn, key);
    if (id === null) {
      // Bounded, cancellable retry: the old chain re-armed every 2 s forever and
      // kept a disposed unit (group, capsule, plate, Lua slot) alive per world hop.
      if (this.retryTimer !== null) window.clearTimeout(this.retryTimer);
      this.retryTimer = window.setTimeout(() => {
        this.retryTimer = null;
        if (this.bodyKey === key && !this.body && !this.host.disposedUnit(this)) this.loadBody(key);
      }, 2000);
      return;
    }
    void assets.load(id).then((shared) => {
      if (this.bodyKey !== key || this.host.disposedUnit(this)) { assets.dispose(id); return; }
      this.dropBody();
      const c = shared.clone();
      markShared(c); // the master's buffers belong to the AssetModule cache
      c.name = `npc-body:${key}`;
      this.body = c;
      this.assetId = id;
      this.group.add(c);
      this.capsule.visible = false;
      if (this.tintHex) this.applyTint(this.tintHex);
    }).catch(() => { /* capsule stays */ });
  }

  private retryTimer: number | null = null;

  private dropBody(): void {
    if (this.retryTimer !== null) { window.clearTimeout(this.retryTimer); this.retryTimer = null; }
    if (this.body) { this.group.remove(this.body); deferDisposeTree(this.body); this.body = null; }
    if (this.assetId !== null && this.host.assets) { this.host.assets.dispose(this.assetId); this.assetId = null; }
    this.capsule.visible = true;
  }

  dispose(): void {
    this.group.removeFromParent();
    this.dropBody();
    deferDisposeTree(this.group);
  }

  // ── per-frame ──────────────────────────────────────────────────────────────

  step(dt: number, now: number): void {
    const conn = atlas.conn;
    if (!conn) return;
    const wid = atlas.currentWorldId;
    if (atlas.editMode) {
      // The editor owns transforms: units hold their row pose (the one being
      // dragged belongs to the gizmo).
      if (this.host.heldNpcId !== this.id) { this.x = this.row.posX; this.z = this.row.posZ; this.yaw = this.row.yaw; this.target = null; this.place(conn, wid); }
      this.bubble.visible = false;
      return;
    }
    // Walk the waypoint chain (A*-routed around parts/devices; a straight line when clear).
    if (this.waypoints.length > 0) {
      const wp = this.waypoints[0];
      const dx = wp.x - this.x, dz = wp.z - this.z;
      const d = Math.hypot(dx, dz);
      const stepM = this.speedMps * dt;
      if (d <= Math.max(0.05, stepM)) {
        this.x = wp.x; this.z = wp.z;
        this.waypoints.shift();
        if (this.waypoints.length === 0) {
          this.target = null;
          const last = this.finalTarget; this.finalTarget = null;
          if (this.followName) this.advanceFollow(); else this.push('on_arrive', last?.x ?? this.x, last?.z ?? this.z);
        }
      } else {
        this.x += (dx / d) * stepM; this.z += (dz / d) * stepM;
        this.yaw = Math.atan2(dx, dz); // the avatar GLBs face +Z (the versona renderer's convention)
      }
    }
    // Day schedule (authored in the editor, no code): apply the entry in force when the hour changes.
    if (this.schedule.length > 0) {
      const h = this.hour();
      if (h !== this.scheduleHour) { this.scheduleHour = h; const e = scheduleEntryFor(this.schedule, h); if (e) this.applySchedule(e); }
    }
    if (this.host.talkingUnit === this) this.faceToward(atlas.localX, atlas.localZ);
    this.place(conn, wid);
    // Gesture.
    if (this.emoteKind) {
      const t = (now - this.emoteT0) / 1000;
      if (t >= EMOTE_S) { this.emoteKind = null; this.group.rotation.x = 0; this.group.rotation.z = 0; }
      else {
        const f = t / EMOTE_S;
        switch (this.emoteKind) {
          case 'wave': this.group.rotation.z = Math.sin(f * Math.PI * 6) * 0.12; break;
          case 'nod': this.group.rotation.x = Math.sin(f * Math.PI * 4) * 0.18; break;
          case 'bow': this.group.rotation.x = Math.sin(f * Math.PI) * 0.55; break;
          case 'spin': this.group.rotation.y = this.yaw + f * Math.PI * 2; break;
          case 'jump': this.group.position.y += Math.sin(f * Math.PI) * 0.6; break;
        }
      }
    }
    // Bubble fade.
    if (this.bubble.visible) {
      const left = this.bubbleUntil - now;
      if (left <= 0) this.bubble.visible = false;
      else this.bubble.material.opacity = Math.min(1, left / 600);
    }
    // Senses — the LOCAL player.
    const d = this.playerDist();
    const inside = d <= this.senseR;
    if (inside !== this.playerInside) {
      this.playerInside = inside;
      if (inside) this.push('on_player_enter', atlas.username ?? 'traveler', Math.round(d * 10) / 10);
      else this.push('on_player_leave', atlas.username ?? 'traveler');
    }
    const hour = this.hour();
    if (this.lastHour === -1) this.lastHour = hour;
    else if (hour !== this.lastHour) { this.lastHour = hour; this.push('on_hour', hour); }
  }

  private place(conn: NonNullable<typeof atlas.conn>, wid: bigint | null): void {
    this.y = groundYIn(conn, wid, this.x, this.z);
    this.group.position.set(this.x, this.y, this.z);
    if (this.emoteKind !== 'spin') this.group.rotation.y = this.yaw;
    this.group.visible = !this.hidden;
  }

  private faceToward(x: number, z: number): void {
    this.yaw = Math.atan2(x - this.x, z - this.z); // +Z-forward bodies: atan2(dx, dz) looks AT the target
  }

  push(hook: string, ...args: unknown[]): void {
    if (this.events.length >= EVENT_CAP) this.events.shift();
    this.events.push({ hook, args });
    if (hook !== 'on_hour') this.note(`← ${hook}(${args.map((a) => JSON.stringify(a)).join(', ').slice(0, 80)})`);
  }

  drain(): NpcEvent[] { return this.events.splice(0, this.events.length); }

  private refreshPlate(): void {
    const key = `${this.displayName}|${this.stateLabel}`;
    if (key === this.plateKey) return;
    this.plateKey = key;
    const lines = [this.displayName];
    if (this.stateLabel && this.stateLabel !== 'idle') lines.push(this.stateLabel);
    paintSprite(this.plate, lines, { bg: 'rgba(10,14,22,0.78)', fg: '#eaf6ff', sub: '#9fd8b0', heightM: 0.36 + (lines.length > 1 ? 0.18 : 0) });
  }

  private applyTint(hex: string): void {
    const color = new THREE.Color(`#${hex.replace(/^#/, '')}`);
    const target = this.body ?? this.capsule;
    target.traverse((o) => {
      const m = o as THREE.Mesh & { material?: THREE.Material | THREE.Material[] };
      if (!m.isMesh || !m.material) return;
      if (!(m.userData as { ownMat?: boolean }).ownMat) {
        m.material = Array.isArray(m.material) ? m.material.map((mm) => mm.clone()) : m.material.clone();
        (m.userData as { ownMat?: boolean }).ownMat = true;
      }
      for (const mat of Array.isArray(m.material) ? m.material : [m.material]) {
        const sm = mat as THREE.MeshStandardMaterial;
        if (sm.emissive) { sm.emissive.copy(color); sm.emissiveIntensity = 0.35; }
      }
    });
  }

  private clearTint(): void {
    const target = this.body ?? this.capsule;
    target.traverse((o) => {
      const m = o as THREE.Mesh & { material?: THREE.Material | THREE.Material[] };
      if (!m.isMesh || !m.material) return;
      for (const mat of Array.isArray(m.material) ? m.material : [m.material]) {
        const sm = mat as THREE.MeshStandardMaterial;
        if (sm.emissive) { sm.emissive.setRGB(0, 0, 0); sm.emissiveIntensity = 1; }
      }
    });
  }

  private note(what: string): void {
    this.recent.push(`${this.elapsed().toFixed(1)}s ${what}`);
    if (this.recent.length > 60) this.recent.shift();
  }

  /** A structured look at the unit (the copilot's before/after test snapshot). */
  snapshot(): Record<string, unknown> {
    return {
      id: this.id, name: this.displayName, state: this.stateLabel,
      x: Math.round(this.x * 10) / 10, y: Math.round(this.y * 10) / 10, z: Math.round(this.z * 10) / 10, yaw: Math.round(this.yaw * 100) / 100,
      moving: this.target !== null, target: this.target, speed: this.speedMps, hidden: this.hidden,
      talking: this.talking(), greeting: this.greetingText, topics: this.topics.map((t) => ({ key: t.k, label: t.l, canned: t.r !== undefined })),
      choices: this.choices, senseRadius: this.senseR, playerDist: Math.round(this.playerDist() * 10) / 10,
      recent: this.recent.slice(-30),
    };
  }

  // ── the API (one method per npc.* entry; the bridge maps names) ────────────

  playerDist(): number { return Math.hypot(atlas.localX - this.x, atlas.localZ - this.z); }
  name(): string { return this.displayName; }
  setName(t: unknown): boolean { this.displayName = String(t ?? '').slice(0, 32) || this.row.name; this.refreshPlate(); return true; }
  state(): string { return this.stateLabel; }
  setState(t: unknown): boolean { this.stateLabel = String(t ?? '').slice(0, 24); this.refreshPlate(); this.note(`state → ${this.stateLabel}`); return true; }
  configGet(k: unknown): string | number | boolean {
    const v = this.config[String(k ?? '')];
    return typeof v === 'number' || typeof v === 'boolean' ? v : v === undefined || v === null ? '' : typeof v === 'string' ? v : JSON.stringify(v);
  }
  private memKey(): string { return `atlas:npcmem:${atlas.currentWorldId ?? 0}:${this.id}`; }
  private readMem(): Record<string, string | number> { try { return JSON.parse(localStorage.getItem(this.memKey()) ?? '{}') as Record<string, string | number>; } catch { return {}; } }
  memorySet(k: unknown, v: unknown): boolean {
    const key = String(k ?? '').slice(0, 32);
    if (!key) return false;
    const m = this.readMem();
    if (!(key in m) && Object.keys(m).length >= MAX_MEMORY_KEYS) return false;
    m[key] = typeof v === 'number' && Number.isFinite(v) ? v : String(v ?? '').slice(0, 200);
    try { localStorage.setItem(this.memKey(), JSON.stringify(m)); return true; } catch { return false; }
  }
  memoryGet(k: unknown): string | number { const v = this.readMem()[String(k ?? '')]; return v === undefined ? '' : v; }
  homeX(): number { return this.row.posX; }
  homeZ(): number { return this.row.posZ; }
  distHome(): number { return Math.hypot(this.row.posX - this.x, this.row.posZ - this.z); }
  goto(x: unknown, z: unknown, speed?: unknown): boolean {
    const tx = Number(x), tz = Number(z);
    if (!Number.isFinite(tx) || !Number.isFinite(tz)) return false;
    if (speed !== undefined && speed !== null) this.setSpeed(speed);
    this.followName = null;
    return this.walkTo(tx, tz);
  }
  /** Route + walk (used by goto, wander, home, schedules and path following). */
  private walkTo(tx: number, tz: number): boolean {
    const route = this.pathing ? this.host.routeFor(this, tx, tz) : [];
    this.waypoints = route.length ? route : [{ x: tx, z: tz }];
    this.finalTarget = { x: tx, z: tz };
    this.target = { x: tx, z: tz };
    this.note(`goto (${tx.toFixed(1)}, ${tz.toFixed(1)}) @ ${this.speedMps} m/s${route.length > 1 ? ` via ${route.length} waypoints` : ''}`);
    return true;
  }
  stop(): boolean { this.target = null; this.waypoints = []; this.finalTarget = null; this.followName = null; return true; }
  moving(): boolean { return this.waypoints.length > 0; }
  setPathing(b: unknown): boolean { this.pathing = !!b; return true; }
  /** Walk a `path` device's waypoints (loops when the path loops); on_arrive fires at the end of a non-looping path. */
  followPath(name: unknown): boolean {
    const key = String(name ?? '');
    const pts = this.host.pathPoints(key);
    if (pts.length === 0) return false;
    this.followName = key; this.followIdx = 0;
    this.note(`follow path "${key}" (${pts.length} points)`);
    return this.walkTo(pts[0].x, pts[0].z);
  }
  private advanceFollow(): void {
    const name = this.followName!;
    const pts = this.host.pathPoints(name);
    if (pts.length === 0) { this.followName = null; return; }
    this.followIdx++;
    if (this.followIdx >= pts.length) {
      if (this.host.pathLoops(name)) this.followIdx = 0;
      else { this.followName = null; this.push('on_arrive', this.x, this.z); return; }
    }
    const p = pts[this.followIdx];
    this.walkTo(p.x, p.z);
  }
  following(): string { return this.followName ?? ''; }
  private applySchedule(e: ScheduleEntry): void {
    this.note(`schedule ${e.hour}:00 → ${e.action}${e.value ? ` ${e.value}` : ''}`);
    switch (e.action) {
      case 'goto': if (e.x !== undefined && e.z !== undefined) this.walkTo(e.x, e.z); break;
      case 'home': this.followName = null; this.walkTo(this.row.posX, this.row.posZ); break;
      case 'wander': this.wander(e.x ?? 6); break;
      case 'state': this.setState(e.value ?? ''); break;
      case 'path': this.followPath(e.value ?? ''); break;
      case 'say': this.say(e.value ?? '', 5); break;
    }
  }
  // ── data-driven dialogue (Edit Mode tree editor / npc.dialogue) ──
  loadDialogue(json: unknown): boolean {
    let raw: unknown = json;
    if (typeof json === 'string') { try { raw = JSON.parse(json); } catch { return false; } }
    const t = parseDialogue(raw);
    if (!t) { this.tree = null; this.node = null; return false; }
    this.tree = t; this.node = null;
    this.note(`dialogue loaded (${t.nodes.length} nodes)`);
    return true;
  }
  clearDialogue(): boolean { this.tree = null; this.node = null; return true; }
  hasDialogue(): boolean { return this.tree !== null; }
  startDialogue(): boolean {
    if (!this.tree) return false;
    if (!this.talking()) { if (this.playerDist() > TALK_OPEN_M) return false; this.host.openTalk(this); }
    this.showNode(this.tree.start);
    return true;
  }
  private runActions(actions: string[] | undefined): void {
    for (const a of actions ?? []) {
      const p = parseAction(a);
      switch (p.op) {
        case 'mem': this.memorySet(p.key, /^-?\d+(\.\d+)?$/.test(p.value) ? Number(p.value) : p.value); break;
        case 'state': this.setState(p.value); break;
        case 'signal': this.host.signal(this, p.key, p.value); break;
        case 'say': this.say(p.value, 5); break;
        case 'emote': this.emote(p.value); break;
        case 'goto': { const m = /^(-?[\d.]+)\s+(-?[\d.]+)/.exec(p.value); if (m) this.walkTo(Number(m[1]), Number(m[2])); break; }
        default: break;
      }
    }
  }
  private showNode(id: string): void {
    if (!this.tree) return;
    const n = dialogueNode(this.tree, id);
    if (!n) { this.node = null; this.choices = null; this.host.topicsChanged(this); return; }
    this.node = n;
    this.runActions(n.actions);
    if (n.text) this.say(n.text, 8);
    const vis = visibleChoices(n, (k) => this.memoryGet(k));
    this.choices = vis.length ? vis.map((c) => c.text) : null;
    this.host.topicsChanged(this);
  }
  /** A choice button was pressed — a tree choice advances the tree; otherwise the script hears on_choice. */
  pickChoice(index: number, label: string, player: string): void {
    const n = this.node;
    if (n && this.tree) {
      const vis = visibleChoices(n, (k) => this.memoryGet(k));
      const c = vis[index - 1];
      this.choices = null;
      this.push('on_choice', index, label, player);
      if (!c) { this.node = null; this.host.topicsChanged(this); return; }
      this.runActions(c.actions);
      if (c.next) this.showNode(c.next); else { this.node = null; this.host.topicsChanged(this); }
      return;
    }
    this.choices = null;
    this.push('on_choice', index, label, player);
  }
  loadSchedule(json: unknown): boolean {
    let raw: unknown = json;
    if (typeof json === 'string') { try { raw = JSON.parse(json); } catch { return false; } }
    this.schedule = parseSchedule(raw); this.scheduleHour = -1;
    return this.schedule.length > 0;
  }
  clearSchedule(): boolean { this.schedule = []; return true; }
  home(): boolean { return this.goto(this.row.posX, this.row.posZ); }
  wander(r: unknown): boolean {
    const rad = Math.min(60, Math.max(1, Number(r) || 5));
    const a = this.rng() * Math.PI * 2, d = Math.sqrt(this.rng()) * rad;
    return this.goto(this.row.posX + Math.cos(a) * d, this.row.posZ + Math.sin(a) * d);
  }
  teleport(x: unknown, z: unknown): boolean {
    const tx = Number(x), tz = Number(z);
    if (!Number.isFinite(tx) || !Number.isFinite(tz)) return false;
    this.x = tx; this.z = tz; this.target = null; this.note(`teleport (${tx.toFixed(1)}, ${tz.toFixed(1)})`); return true;
  }
  face(x: unknown, z: unknown): boolean { this.faceToward(Number(x) || 0, Number(z) || 0); return true; }
  facePlayer(): boolean { this.faceToward(atlas.localX, atlas.localZ); return true; }
  setSpeed(s: unknown): boolean { this.speedMps = Math.min(12, Math.max(0.2, Number(s) || DEFAULT_SPEED)); return true; }
  speed(): number { return this.speedMps; }
  emote(kind: unknown): boolean {
    const k = String(kind ?? '');
    if (!['wave', 'nod', 'bow', 'spin', 'jump'].includes(k)) return false;
    this.emoteKind = k; this.emoteT0 = performance.now(); this.note(`emote ${k}`); return true;
  }
  emoting(): boolean { return this.emoteKind !== null; }
  hide(): boolean { this.hidden = true; return true; }
  show(): boolean { this.hidden = false; return true; }
  visible(): boolean { return !this.hidden; }
  setScale(s: unknown): boolean { this.scaleMul = Math.min(4, Math.max(0.25, Number(s) || 1)) / Math.max(0.01, this.row.scale); this.group.scale.setScalar(this.row.scale * this.scaleMul); return true; }
  tint(hex: unknown): boolean { const h = String(hex ?? '').replace(/^#/, ''); if (!/^[0-9a-fA-F]{6}$/.test(h)) return false; this.tintHex = h; this.applyTint(h); return true; }
  untint(): boolean { this.tintHex = null; this.clearTint(); return true; }
  say(text: unknown, seconds?: unknown): boolean {
    const t = String(text ?? '').slice(0, BUBBLE_MAX);
    if (!t) return false;
    const secs = Math.min(30, Math.max(0.5, Number(seconds) || 4));
    paintSprite(this.bubble, wrapText(t, 34), { bg: 'rgba(236,244,255,0.94)', fg: '#13203a', font: 30, heightM: 0.3 });
    this.bubble.material.opacity = 1;
    this.bubbleUntil = performance.now() + secs * 1000;
    this.host.onUnitSaid(this, t);
    this.note(`say "${t.slice(0, 80)}"`);
    return true;
  }
  setGreeting(t: unknown): boolean { this.greetingText = String(t ?? '').slice(0, 240); this.note(`greeting "${this.greetingText.slice(0, 60)}"`); return true; }
  addTopic(k: unknown, l: unknown, r?: unknown): boolean {
    const key = String(k ?? '').slice(0, 32), label = String(l ?? '').slice(0, 48);
    if (!key || !label) return false;
    const reply = r === undefined || r === null ? undefined : String(r).slice(0, 400);
    const i = this.topics.findIndex((t) => t.k === key);
    if (i >= 0) this.topics[i] = { k: key, l: label, r: reply };
    else { if (this.topics.length >= MAX_TOPICS) return false; this.topics.push({ k: key, l: label, r: reply }); }
    this.host.topicsChanged(this);
    this.note(`topic +${key}${reply !== undefined ? ' (canned)' : ''}`);
    return true;
  }
  removeTopic(k: unknown): boolean { const n = this.topics.length; this.topics = this.topics.filter((t) => t.k !== String(k ?? '')); this.host.topicsChanged(this); return this.topics.length !== n; }
  clearTopics(): boolean { this.topics = []; this.host.topicsChanged(this); return true; }
  ask(q: unknown, ...opts: unknown[]): boolean {
    const labels = opts.filter((o) => o !== undefined && o !== null).map((o) => String(o).slice(0, 48)).slice(0, 4);
    if (labels.length < 2) return false;
    this.say(String(q ?? '').slice(0, BUBBLE_MAX), 8);
    this.choices = labels;
    this.host.topicsChanged(this);
    this.note(`ask [${labels.join(' | ')}]`);
    return true;
  }
  setTalkable(b: unknown): boolean { this.talkable = !!b; return true; }
  setPrompt(t: unknown): boolean { this.promptText = String(t ?? '').slice(0, 60) || 'Ask about anything…'; this.host.topicsChanged(this); return true; }
  talking(): boolean { return this.host.talkingUnit === this; }
  talkingTo(): string { return this.talking() ? atlas.username ?? 'traveler' : ''; }
  openTalk(): boolean { if (this.playerDist() > TALK_OPEN_M) return false; this.host.openTalk(this); return true; }
  endTalk(): boolean { if (!this.talking()) return false; this.host.closeTalk(); return true; }
  hour(): number { return Math.floor(this.host.timeOfDay() * 24) % 24; }
  isNight(): boolean { const h = this.hour(); return h >= 21 || h < 5; }
  elapsed(): number { return (performance.now() - this.bornAt) / 1000; }
  timer(name: unknown, seconds: unknown): boolean {
    const k = String(name ?? ''), s = Math.max(0.05, Number(seconds) || 1);
    const now = this.elapsed();
    const next = this.timers.get(k);
    if (next === undefined) { this.timers.set(k, now + s); return false; }
    if (now >= next) { this.timers.set(k, now + s); return true; }
    return false;
  }
  chance(p: unknown): boolean { return this.rng() < Math.min(1, Math.max(0, Number(p) || 0)); }
  random(a: unknown, b: unknown): number { const lo = Number(a) || 0, hi = Number(b) || 0; return lo + (hi - lo) * this.rng(); }
  slope(): number {
    const conn = atlas.conn;
    if (!conn) return 0;
    const wid = atlas.currentWorldId;
    const e = 1;
    const dx = groundYIn(conn, wid, this.x + e, this.z) - groundYIn(conn, wid, this.x - e, this.z);
    const dz = groundYIn(conn, wid, this.x, this.z + e) - groundYIn(conn, wid, this.x, this.z - e);
    const g = Math.hypot(dx, dz) / (2 * e);
    return Math.min(1, g / (1 + g));
  }
  gotoVersona(id: unknown): boolean {
    const v = this.host.versonaById(String(id ?? ''));
    if (!v) return false;
    const dx = v.x - this.x, dz = v.z - this.z, d = Math.hypot(dx, dz);
    if (d < 2) return true;
    return this.goto(v.x - (dx / d) * 1.8, v.z - (dz / d) * 1.8);
  }
  versonaAsk(id: unknown, text: unknown): boolean {
    const vid = String(id ?? '');
    const t = String(text ?? '').trim().slice(0, 240);
    if (!vid || !t || !this.host.versonaById(vid)) return false;
    this.askedVersonas.add(vid);
    this.note(`versona_ask #${vid} "${t.slice(0, 60)}"`);
    return this.host.askVersona(this, vid, t);
  }
}

export class NpcUnitModule implements IModule {
  readonly name = 'NpcUnit';
  assets: AssetModule | null = null;
  private scene!: THREE.Scene;
  readonly root = new THREE.Group();
  readonly versonaMarkerRoot = new THREE.Group();
  private units = new Map<string, NpcUnit>();
  private disposed = new WeakSet<NpcUnit>();
  private sub: { unsubscribe(): void } | null = null;
  private subWorld: bigint | null = null;
  /** The unit Edit Mode currently holds in a gizmo (its pose is the editor's). */
  heldNpcId: string | null = null;
  // talk window
  private win: UiWindow | null = null;
  private log: HTMLDivElement | null = null;
  private head: HTMLDivElement | null = null;
  private topicsPane: HTMLDivElement | null = null;
  private input: HTMLInputElement | null = null;
  talkingUnit: NpcUnit | null = null;
  // player motion tracking (speed / moving)
  private lastPx = 0; private lastPz = 0; private playerSpeedEma = 0;
  // per-frame caches
  private frame = 0;
  private versonaCacheFrame = -1;
  private versonaCache: VersonaLite[] = [];
  private partCacheFrame = -1;
  private partCache: Array<{ id: string; x: number; z: number }> = [];
  private markers = new Map<string, THREE.Mesh>();
  private markerGeo: THREE.CapsuleGeometry | null = null;
  private markerMat: THREE.MeshStandardMaterial | null = null;
  private sitesSeed = '';
  private sites: SettlementSite[] = [];
  private versionSig = '';
  /** Bumped on every npc_unit row change — the runtime polls this number and rebuilds the signature string only then (2026-09-12). */
  scriptVersion = 0;

  constructor(assets: AssetModule | null = null, private readonly devices: DeviceModule | null = null) { this.assets = assets; }

  /** A unit's route to (x, z): grid A* around build parts, placements and solid devices (empty = walk straight). */
  routeFor(u: NpcUnit, x: number, z: number): Array<{ x: number; z: number }> {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return [];
    const obstacles: Array<{ x: number; z: number; hx: number; hz: number }> = [];
    try {
      for (const p of conn.db.part.iter()) {
        if (p.worldId !== wid || !p.physics) continue;
        const hx = Math.max(0.2, (p.primType === 'mesh' ? 0.9 : 0.5) * Math.abs(p.scaleX)), hz = Math.max(0.2, (p.primType === 'mesh' ? 0.9 : 0.5) * Math.abs(p.scaleZ));
        if (Math.hypot(p.posX - u.x, p.posZ - u.z) > 90) continue;
        obstacles.push({ x: p.posX, z: p.posZ, hx, hz });
      }
      for (const r of conn.db.adminPlacement.iter()) {
        if (r.worldId !== wid || Math.hypot(r.posX - u.x, r.posZ - u.z) > 90) continue;
        const h = Math.max(0.3, 0.9 * Math.abs(r.scaleX));
        obstacles.push({ x: r.posX, z: r.posZ, hx: h, hz: h });
      }
    } catch { /* churn */ }
    if (this.devices) for (const o of this.devices.obstacles()) obstacles.push(o);
    if (obstacles.length === 0) return [];
    return findPath({ x: u.x, z: u.z }, { x, z }, obstacles, { cell: 1, radius: 64, agent: 0.45 });
  }
  pathPoints(name: string): Array<{ x: number; z: number }> { return this.devices?.pathPoints(name) ?? []; }
  pathLoops(name: string): boolean { return this.devices?.pathLoops(name) ?? true; }
  /** A dialogue action or script broadcast from a unit → the world signal bus. */
  signal(u: NpcUnit, topic: string, payload: string): void { bus.emit('atlas:signal', { topic: topic.slice(0, 64), payload: payload.slice(0, 400), from: `script:${u.id}` }); }

  init(app: App): void {
    this.scene = app.world.scene;
    this.scene.add(this.root, this.versonaMarkerRoot);
    bus.on('atlas:account-ready', () => this.bind());
    bus.on('atlas:world-entered', () => this.onWorld());
    // (bug sweep) the chat sub is (re)laid from onWorld — atlas:world-cleared
    // fires while currentWorldId STILL names the world being left, so
    // subscribing here pinned the speech-bubble rows to the OLD world.
    bus.on('atlas:world-cleared', () => { this.teardown(); this.closeTalk(); });
    window.addEventListener('keydown', (e) => {
      const t = e.target instanceof Element ? (e.target as HTMLElement) : null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable || !!t.closest('.cm-editor'))) return;
      if (e.code !== 'KeyE' || e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
      if (atlas.editMode || atlas.flyMode || atlas.spaceFlight || atlas.loadingGate) return;
      this.interact();
    });
    app.world.addSystem(makeSystem('NpcUnitStep', Phase.Animation, 44, (dt) => this.step(dt)));
    (window as unknown as Record<string, unknown>).__npc = {
      units: () => [...this.units.values()].map((u) => ({ id: u.id, name: u.name(), state: u.state(), x: u.x, z: u.z, topics: u.topics.length, moving: u.moving(), following: u.following(), hasDialogue: u.hasDialogue() })),
      talk: (id: string) => { const u = this.units.get(String(id)); if (u) this.openTalk(u); return !!u; },
      closeTalk: () => this.closeTalk(),
      talking: () => this.talkingUnit?.id ?? null,
      say: (id: string, text: string) => { const u = this.units.get(String(id)); if (u) u.say(text); return !!u; },
      events: (id: string) => this.units.get(String(id))?.events.length ?? -1,
      // ── probe surface ──
      snapshot: (id: string) => this.units.get(String(id))?.snapshot() ?? null,
      topics: (id: string) => this.units.get(String(id))?.topics.map((t) => ({ key: t.k, label: t.l, canned: t.r !== undefined })) ?? [],
      choices: (id: string) => this.units.get(String(id))?.choices ?? null,
      pick: (id: string, i: number) => { const u = this.units.get(String(id)); if (!u || !u.choices) return false; const label = u.choices[i - 1] ?? ''; u.pickChoice(i, label, atlas.username ?? 'probe'); if (this.talkingUnit === u) this.renderTopics(u); return true; },
      pickTopic: (id: string, key: string) => { const u = this.units.get(String(id)); const t = u?.topics.find((x) => x.k === key); if (!u || !t) return false; this.pickTopic(u, t); return true; },
      sayTo: (id: string, text: string) => { const u = this.units.get(String(id)); if (!u) return false; u.push('on_say', atlas.username ?? 'probe', text); return true; },
      /** Call any npc.* API function AS a unit (the same table the Lua bridge gets). */
      call: (id: string, fn: string, ...args: unknown[]) => { const u = this.units.get(String(id)); if (!u) return null; const api = this.buildApi(() => u, (t, p) => { this.signal(u, String(t ?? ''), String(p ?? '')); return true; }); const f = api[fn]; return typeof f === 'function' ? f(...args) : null; },
    };
  }

  private chatSub: { unsubscribe(): void } | null = null;
  private chatSubWorld: bigint | null = null;
  /** Speech bubbles only need THIS world's chat rows (bug sweep: the
   *  whole-table sub duplicated the rows a third time across modules).
   *  Subscribe-new-then-unsubscribe-old (the AOI law) so the row cache never gaps. */
  private subChatForWorld(): void {
    const wid = atlas.currentWorldId;
    if (wid === null || wid === this.chatSubWorld) return;
    const prev = this.chatSub;
    try {
      this.chatSub = atlas.conn?.subscriptionBuilder()
        .onApplied(() => { try { prev?.unsubscribe(); } catch { /* gone */ } })
        .subscribe([`SELECT * FROM versona_chat WHERE world_id = ${wid}`]) as { unsubscribe(): void } | null;
      this.chatSubWorld = wid;
    } catch { /* pre-publish server */ }
  }

  private bind(): void {
    const conn = atlas.conn;
    if (!conn) return;
    try {
      conn.subscriptionBuilder().subscribe(['SELECT * FROM versona_profile']);
    } catch { /* pre-publish server */ }
    conn.db.npcUnit.onInsert((_c, row) => this.onRow(row as unknown as NpcUnitRow));
    conn.db.npcUnit.onUpdate((_c, _old, row) => this.onRow(row as unknown as NpcUnitRow));
    conn.db.npcUnit.onDelete((_c, row) => this.onRowGone(row as unknown as NpcUnitRow));
    conn.db.versonaChat.onInsert((_c, row) => {
      if (row.speaker !== 'npc') return;
      const vid = row.versonaId.toString();
      for (const u of this.units.values()) {
        if (!u.askedVersonas.has(vid)) continue;
        u.versonaLast.set(vid, row.text);
        u.push('on_versona_reply', vid, row.text);
      }
    });
    this.onWorld();
  }

  private onWorld(): void {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null || wid === this.subWorld) return;
    this.teardown();
    this.subWorld = wid;
    this.subChatForWorld(); // chat scoped to the world just ENTERED (currentWorldId is final here)
    const prev = this.sub;
    try {
      this.sub = conn.subscriptionBuilder()
        .onApplied(() => { if (prev) prev.unsubscribe(); this.resync(); })
        .subscribe([`SELECT * FROM npc_unit WHERE world_id = ${wid}`]);
    } catch { /* pre-publish server */ }
    this.resync();
  }

  /** Rebuild the unit set from the row cache (world entry / subscription applied). */
  private resync(): void {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return;
    const seen = new Set<string>();
    try {
      for (const r of conn.db.npcUnit.iter()) {
        if (r.worldId !== wid) continue;
        seen.add(r.npcId.toString());
        this.onRow(r as unknown as NpcUnitRow);
      }
    } catch { /* churn */ }
    for (const [id, u] of this.units) if (!seen.has(id)) { this.disposed.add(u); u.dispose(); this.units.delete(id); }
  }

  private onRow(row: NpcUnitRow): void {
    if (row.worldId !== atlas.currentWorldId) return;
    this.scriptVersion++;
    const id = row.npcId.toString();
    const u = this.units.get(id);
    if (u) u.applyRow(row, false);
    else {
      const nu = new NpcUnit(row, this);
      this.units.set(id, nu);
      this.root.add(nu.group);
    }
  }

  private onRowGone(row: NpcUnitRow): void {
    this.scriptVersion++;
    const id = row.npcId.toString();
    const u = this.units.get(id);
    if (!u) return;
    if (this.talkingUnit === u) this.closeTalk();
    this.disposed.add(u);
    u.dispose();
    this.units.delete(id);
  }

  private teardown(): void {
    for (const u of this.units.values()) { this.disposed.add(u); u.dispose(); }
    this.units.clear();
    this.subWorld = null;
    this.clearMarkers();
  }

  disposedUnit(u: NpcUnit): boolean { return this.disposed.has(u); }

  // ── queries the runtime + Edit Mode use ────────────────────────────────────

  ids(): string[] { return [...this.units.keys()].sort((a, b) => (BigInt(a) < BigInt(b) ? -1 : 1)); }
  get(id: string): NpcUnit | undefined { return this.units.get(id); }
  /** Signature of the enabled units' scripts — the runtime's rebuild detector. */
  scriptSignature(): string {
    const parts: string[] = [];
    for (const id of this.ids()) {
      const u = this.units.get(id)!;
      if (u.row.enabled && u.row.scriptSrc.trim()) parts.push(`${id}:${u.row.updatedAt.microsSinceUnixEpoch}`);
    }
    this.versionSig = parts.join(',');
    return this.versionSig;
  }
  unitMesh(id: string): THREE.Object3D | null { return this.units.get(id)?.group ?? null; }
  /** A placed versona's edit marker (pickable) → its id. */
  markerFor(versonaId: string): THREE.Object3D | null { return this.markers.get(versonaId) ?? null; }

  timeOfDay(): number {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return 0.5;
    try { return conn.db.skyState.worldId.find(wid)?.timeOfDay ?? 0.5; } catch { return 0.5; }
  }
  weather(): string {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return '';
    try { return conn.db.skyState.worldId.find(wid)?.weather ?? ''; } catch { return ''; }
  }
  private worldRow(): { name: string; sizeM: number; terrainSeed: string; kind: string } | null {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return null;
    return conn.db.world.worldId.find(wid) ?? null;
  }
  isCommons(): boolean { return atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId; }
  biomeUnder(x: number, z: number): string { const w = this.worldRow(); return w ? biomeAt(w.terrainSeed, x, z) : ''; }
  inWater(x: number, z: number): boolean {
    const w = this.worldRow();
    if (!w || !this.isCommons()) return false;
    return terrainHeight(w.terrainSeed, x, z) < commonsSeaLevel(w.terrainSeed);
  }
  worldName(): string { return this.worldRow()?.name ?? ''; }
  worldSize(): number { return this.worldRow()?.sizeM ?? 0; }
  playerCount(): number {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return 0;
    let n = 0;
    try { for (const p of conn.db.playerState.iter()) if (p.worldId === wid) n++; } catch { /* churn */ }
    return n;
  }
  playersNear(x: number, z: number, r: number): number {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return 0;
    let n = 0;
    try { for (const p of conn.db.playerState.iter()) if (p.worldId === wid && Math.hypot(p.posX - x, p.posZ - z) <= r) n++; } catch { /* churn */ }
    // The local player may not be in the (AOI-windowed) cache — count them by position.
    if (Math.hypot(atlas.localX - x, atlas.localZ - z) <= r) n = Math.max(n, 1);
    return n;
  }
  playerMode(): string {
    if (atlas.spaceFlight) return 'space';
    if (atlas.skyFlight) return 'fly';
    if (atlas.hoverRide) return 'hover';
    if (atlas.editMode) return 'edit';
    return 'walk';
  }
  playerSpeed(): number { return this.playerSpeedEma; }
  playerFacing(x: number, z: number, deg: number): boolean {
    const cam = atlas.localMesh?.children[0] as THREE.Object3D | undefined;
    if (!cam) return false;
    const fwd = cam.getWorldDirection(new THREE.Vector3());
    const to = new THREE.Vector3(x - atlas.localX, 0, z - atlas.localZ);
    if (to.lengthSq() < 1e-6) return true;
    fwd.y = 0;
    if (fwd.lengthSq() < 1e-6) return false;
    const ang = fwd.normalize().angleTo(to.normalize());
    return ang <= THREE.MathUtils.degToRad(Math.min(180, Math.max(5, deg)) / 2);
  }
  playerIsOwner(): boolean {
    const w = atlas.conn && atlas.currentWorldId !== null ? atlas.conn.db.world.worldId.find(atlas.currentWorldId) : null;
    return !!w && !!atlas.identityHex && w.owner.toHexString() === atlas.identityHex;
  }
  playerMet(u: NpcUnit): boolean { return u.memoryGet('__met') === 1; }

  parts(): Array<{ id: string; x: number; z: number }> {
    if (this.partCacheFrame === this.frame) return this.partCache;
    this.partCacheFrame = this.frame;
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    this.partCache = [];
    if (!conn || wid === null) return this.partCache;
    try { for (const p of conn.db.part.iter()) if (p.worldId === wid) this.partCache.push({ id: p.partId.toString(), x: p.posX, z: p.posZ }); } catch { /* churn */ }
    return this.partCache;
  }

  versonas(): VersonaLite[] {
    if (this.versonaCacheFrame === this.frame) return this.versonaCache;
    this.versonaCacheFrame = this.frame;
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    this.versonaCache = [];
    if (!conn || wid === null) return this.versonaCache;
    try {
      const hot = new Map<string, { curX: number; curZ: number; state: string }>();
      for (const h of conn.db.versonaState.iter()) if (h.worldId === wid) hot.set(h.versonaId.toString(), h);
      const agents = new Map<string, { job: string; mood: number }>();
      try { for (const a of conn.db.npcAgent.iter()) if (a.worldId === wid) agents.set(a.versonaId.toString(), a); } catch { /* lazy sub */ }
      const profiles = new Map<string, string>();
      for (const p of conn.db.versonaProfile.iter()) if (p.worldId === wid && p.name) profiles.set(p.versonaId.toString(), p.name);
      for (const v of conn.db.versona.iter()) {
        if (v.worldId !== wid) continue;
        const id = v.versonaId.toString();
        const h = hot.get(id);
        // AOI law: the hot row's presence means "near enough to matter" on the
        // Commons; personal worlds are small — every row counts.
        if (!h && this.isCommons()) continue;
        const a = agents.get(id);
        this.versonaCache.push({
          id, name: profiles.get(id) ?? nameFromTables(conn, v.versonaId),
          x: h ? h.curX : v.curX, z: h ? h.curZ : v.curZ, state: h ? h.state : v.state,
          job: a?.job ?? v.archetype, mood: a?.mood ?? 50,
        });
      }
      this.versonaCache.sort((a, b) => (BigInt(a.id) < BigInt(b.id) ? -1 : 1));
    } catch { /* churn */ }
    return this.versonaCache;
  }
  versonaById(id: string): VersonaLite | null { return this.versonas().find((v) => v.id === id) ?? null; }

  /** Commons settlements (cached per seed) for nearest_town. */
  nearestTown(x: number, z: number): { name: string; dist: number } | null {
    const conn = atlas.conn;
    const w = this.worldRow();
    if (!conn || !w || !this.isCommons()) return null;
    if (this.sitesSeed !== w.terrainSeed) { this.sitesSeed = w.terrainSeed; this.sites = settlementSites(w.terrainSeed); }
    let best: SettlementSite | null = null, bd = Infinity;
    for (const s of this.sites) { const d = Math.hypot(s.centerX - x, s.centerZ - z); if (d < bd) { bd = d; best = s; } }
    return best ? { name: resolvedCityName(best, conn), dist: bd } : null;
  }
  warActive(): boolean {
    const conn = atlas.conn;
    if (!conn || !this.isCommons()) return false;
    try { for (const w of conn.db.warState.iter()) if (w.status === 'active') return true; } catch { /* lazy sub */ }
    return false;
  }

  /** Host-only write: anyone who may edit this world drives NPC↔versona talk. */
  askVersona(u: NpcUnit, vid: string, text: string): boolean {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return false;
    const caps = capsIn(wid);
    if (!caps.build && !caps.admin) return false;
    conn.reducers.npcAskVersona({ npcId: u.row.npcId, versonaId: BigInt(vid), text })
      .catch((e: Error) => bus.emit('atlas:script-log', { script: `npc:${u.name()}`, msg: `versona_ask refused: ${e.message}`, level: 'error' }));
    return true;
  }

  // ── per-frame ──────────────────────────────────────────────────────────────

  private step(dt: number): void {
    this.frame++;
    const now = performance.now();
    const dx = atlas.localX - this.lastPx, dz = atlas.localZ - this.lastPz;
    this.lastPx = atlas.localX; this.lastPz = atlas.localZ;
    const inst = dt > 0 ? Math.min(60, Math.hypot(dx, dz) / dt) : 0;
    this.playerSpeedEma += (inst - this.playerSpeedEma) * Math.min(1, dt * 6);
    for (const u of this.units.values()) u.step(dt, now);
    if (this.talkingUnit && this.talkingUnit.playerDist() > TALK_OPEN_M * 2 && !atlas.editMode) this.closeTalk();
    if (atlas.editMode) this.syncMarkers(); else if (this.markers.size > 0) this.clearMarkers();
  }

  private interact(): void {
    let best: NpcUnit | null = null, bd = INTERACT_M;
    for (const u of this.units.values()) { const d = u.playerDist(); if (d <= bd && u.visible()) { bd = d; best = u; } }
    if (!best) return;
    best.push('on_interact', atlas.username ?? 'traveler', Math.round(bd * 10) / 10);
    if (best.talkable) this.openTalk(best);
  }

  // ── edit markers for placed versonas (pickable by Edit Mode) ───────────────

  private syncMarkers(): void {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return;
    if (!this.markerGeo) { this.markerGeo = new THREE.CapsuleGeometry(0.36, 1.2, 6, 12); this.markerMat = new THREE.MeshStandardMaterial({ color: MARKER_COLOR, transparent: true, opacity: 0.45, emissive: MARKER_COLOR, emissiveIntensity: 0.25, depthWrite: false }); }
    if (this.markerMat) markMrtOverlay(this.markerMat); // a translucent capsule over a unit (idempotent)
    const seen = new Set<string>();
    try {
      for (const p of conn.db.versonaProfile.iter()) {
        if (p.worldId !== wid) continue;
        const id = p.versonaId.toString();
        seen.add(id);
        const hot = conn.db.versonaState.versonaId.find(p.versonaId);
        const v = conn.db.versona.versonaId.find(p.versonaId);
        const x = hot ? hot.curX : v ? v.curX : 0, z = hot ? hot.curZ : v ? v.curZ : 0;
        let m = this.markers.get(id);
        if (!m) {
          m = new THREE.Mesh(this.markerGeo, this.markerMat!);
          m.userData.versonaId = id;
          this.markers.set(id, m);
          this.versonaMarkerRoot.add(m);
        }
        if (this.heldNpcId !== `versona:${id}`) m.position.set(x, groundYIn(conn, wid, x, z) + 0.95, z);
      }
    } catch { /* churn */ }
    for (const [id, m] of this.markers) if (!seen.has(id)) { this.versonaMarkerRoot.remove(m); this.markers.delete(id); }
  }

  private clearMarkers(): void {
    for (const m of this.markers.values()) this.versonaMarkerRoot.remove(m);
    this.markers.clear();
  }

  // ── conversation window ────────────────────────────────────────────────────

  openTalk(u: NpcUnit): void {
    if (this.talkingUnit && this.talkingUnit !== u) this.closeTalk();
    if (!this.win) this.buildWindow();
    const first = this.talkingUnit !== u;
    this.talkingUnit = u;
    u.memorySet('__met', 1);
    if (this.log) this.log.textContent = '';
    this.win!.setTitle(u.name());
    this.win!.setOpen(true);
    this.renderHead(u);
    if (u.hasDialogue()) { this.talkingUnit = u; u.startDialogue(); }
    else this.npcLine(u.greetingText || '…');
    this.renderTopics(u);
    if (this.input) this.input.placeholder = u.promptText;
    if (first) u.push('on_talk_start', atlas.username ?? 'traveler');
  }

  closeTalk(): void {
    const u = this.talkingUnit;
    this.talkingUnit = null;
    this.win?.setOpen(false);
    if (u) { u.choices = null; u.push('on_talk_end', atlas.username ?? 'traveler'); }
  }

  onUnitSaid(u: NpcUnit, text: string): void { if (this.talkingUnit === u) this.npcLine(text); }
  topicsChanged(u: NpcUnit): void { if (this.talkingUnit === u) { this.renderTopics(u); if (this.input) this.input.placeholder = u.promptText; } }

  private buildWindow(): void {
    this.win = makeWindow({ id: 'npc-unit-talk', title: 'Conversation', width: 540, height: 380, minWidth: 420, minHeight: 280, onClose: () => { if (this.talkingUnit) this.closeTalk(); } });
    const p = document.createElement('div');
    p.style.cssText = 'display:flex;flex-direction:column;height:100%;padding:8px;gap:6px;color:#dde;font:12px/1.5 system-ui;box-sizing:border-box';
    this.win.body.appendChild(p);
    this.head = document.createElement('div');
    this.head.style.cssText = 'font-weight:600;color:#e8f2fc;white-space:nowrap;overflow:hidden;text-overflow:ellipsis';
    p.appendChild(this.head);
    const main = document.createElement('div');
    main.style.cssText = 'flex:1;display:flex;gap:8px;min-height:0';
    this.log = document.createElement('div');
    this.log.id = 'npc-unit-log';
    this.log.style.cssText = 'flex:1;overflow:auto;display:flex;flex-direction:column;gap:7px;padding:6px 8px;background:rgba(8,13,22,0.45);border:1px solid #22334d;border-radius:6px;scrollbar-width:thin';
    main.appendChild(this.log);
    this.topicsPane = document.createElement('div');
    this.topicsPane.id = 'npc-unit-topics';
    this.topicsPane.style.cssText = 'width:150px;flex:none;overflow:auto;display:flex;flex-direction:column;gap:2px;padding:4px;background:rgba(8,13,22,0.45);border:1px solid #22334d;border-radius:6px;scrollbar-width:thin';
    main.appendChild(this.topicsPane);
    p.appendChild(main);
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:5px';
    this.input = document.createElement('input');
    this.input.id = 'npc-unit-input';
    this.input.style.cssText = 'flex:1;background:#0d1420;color:#dde;border:1px solid #35506a;border-radius:5px;padding:5px';
    this.input.onkeydown = (e) => { e.stopPropagation(); if (e.key === 'Enter') this.sendText(); if (e.key === 'Escape') this.input?.blur(); };
    const send = document.createElement('button');
    send.textContent = 'Say';
    send.style.cssText = 'padding:5px 10px;background:#144a2f;color:#d8ffe8;border:1px solid #2c7a4f;border-radius:6px;cursor:pointer';
    send.onclick = () => this.sendText();
    row.append(this.input, send);
    p.appendChild(row);
  }

  private renderHead(u: NpcUnit): void {
    if (!this.head) return;
    const st = u.state() && u.state() !== 'idle' ? ` · ${u.state()}` : '';
    this.head.textContent = `${u.name()}${st}`;
  }

  private renderTopics(u: NpcUnit): void {
    const pane = this.topicsPane;
    if (!pane) return;
    pane.textContent = '';
    this.renderHead(u);
    const mk = (label: string, color: string, fn: () => void, bold = false): HTMLButtonElement => {
      const b = document.createElement('button');
      b.textContent = label;
      b.style.cssText = `text-align:left;padding:3px 6px;background:none;border:0;border-radius:4px;cursor:pointer;font:${bold ? 600 : 400} 12px system-ui;color:${color}`;
      b.onmouseenter = () => { b.style.background = '#1a2940'; };
      b.onmouseleave = () => { b.style.background = 'none'; };
      b.onclick = fn;
      return b;
    };
    if (u.choices) {
      const cap = document.createElement('div'); cap.textContent = 'CHOOSE'; cap.style.cssText = 'color:#c9a24a;font:600 10px system-ui;letter-spacing:1px;padding:2px 4px';
      pane.appendChild(cap);
      u.choices.forEach((label, i) => pane.appendChild(mk(label, '#ffd98c', () => {
        this.playerLine(label);
        u.pickChoice(i + 1, label, atlas.username ?? 'traveler');
        this.renderTopics(u);
      })));
    }
    const cap = document.createElement('div'); cap.textContent = 'TOPICS'; cap.style.cssText = 'color:#5f7590;font:600 10px system-ui;letter-spacing:1px;padding:2px 4px';
    pane.appendChild(cap);
    for (const t of u.topics) pane.appendChild(mk(t.l, '#8db8ef', () => this.pickTopic(u, t)));
    const gap = document.createElement('div'); gap.style.cssText = 'flex:1'; pane.appendChild(gap);
    pane.appendChild(mk('Goodbye', '#cfe3ff', () => this.closeTalk(), true));
  }

  private pickTopic(u: NpcUnit, t: Topic): void {
    this.playerLine(t.l);
    if (t.r !== undefined) u.say(t.r, 6);
    else u.push('on_topic', t.k, atlas.username ?? 'traveler');
  }

  private sendText(): void {
    const u = this.talkingUnit;
    const text = this.input?.value.trim() ?? '';
    if (!u || !text) return;
    if (this.input) this.input.value = '';
    const low = text.toLowerCase();
    const hit = u.topics.find((t) => low.includes(t.l.toLowerCase()) || low.includes(t.k.replace(/_/g, ' ')));
    if (hit) { this.pickTopic(u, hit); return; }
    this.playerLine(text);
    u.push('on_say', atlas.username ?? 'traveler', text.slice(0, 240));
  }

  private npcLine(text: string): void {
    if (!this.log) return;
    const d = document.createElement('div');
    d.style.cssText = 'align-self:flex-start;background:#131c2b;border-radius:8px;padding:5px 8px;max-width:94%;color:#cfe8d8';
    d.textContent = text;
    this.pushLine(d);
  }
  private playerLine(text: string): void {
    if (!this.log) return;
    const d = document.createElement('div');
    d.style.cssText = 'align-self:flex-end;background:#1c2c44;border-radius:8px;padding:5px 8px;max-width:92%';
    d.textContent = text;
    this.pushLine(d);
  }
  private pushLine(d: HTMLDivElement): void {
    if (!this.log) return;
    this.log.appendChild(d);
    while (this.log.childElementCount > 80) this.log.firstElementChild?.remove();
    this.log.scrollTop = this.log.scrollHeight;
  }

  // ── the npc.* Lua table ────────────────────────────────────────────────────

  /**
   * Build the `npc` global for the runtime. `current` resolves the unit whose
   * slot is executing (null inside a plain world script → every call is a
   * harmless default). `signal` is supplied by the runtime (it owns the slots).
   */
  buildApi(current: () => NpcUnit | null, signal: (topic: unknown, payload: unknown) => boolean): Record<string, (...a: unknown[]) => unknown> {
    const U = <T>(f: (u: NpcUnit) => T, dflt: T) => (): T => { const u = current(); return u ? f(u) : dflt; };
    const U1 = <T>(f: (u: NpcUnit, a: unknown) => T, dflt: T) => (a: unknown): T => { const u = current(); return u ? f(u, a) : dflt; };
    const U2 = <T>(f: (u: NpcUnit, a: unknown, b: unknown) => T, dflt: T) => (a: unknown, b: unknown): T => { const u = current(); return u ? f(u, a, b) : dflt; };
    const vOf = (id: unknown): VersonaLite | null => this.versonaById(String(id ?? ''));
    const api: Record<string, (...a: unknown[]) => unknown> = {
      id: U((u) => u.id, ''),
      name: U((u) => u.name(), ''),
      set_name: U1((u, a) => u.setName(a), false),
      state: U((u) => u.state(), ''),
      set_state: U1((u, a) => u.setState(a), false),
      config: U1((u, a) => u.configGet(a), ''),
      memory_set: U2((u, a, b) => u.memorySet(a, b), false),
      memory_get: U1((u, a) => u.memoryGet(a), ''),
      x: U((u) => u.x, 0), y: U((u) => u.y, 0), z: U((u) => u.z, 0), yaw: U((u) => u.yaw, 0),
      home_x: U((u) => u.homeX(), 0), home_z: U((u) => u.homeZ(), 0), dist_home: U((u) => u.distHome(), 0),
      count: () => this.units.size,
      id_at: (i: unknown) => this.ids()[Number(i) - 1] ?? '',
      other_state: (id: unknown) => this.units.get(String(id ?? ''))?.state() ?? '',
      other_x: (id: unknown) => this.units.get(String(id ?? ''))?.x ?? 0,
      other_z: (id: unknown) => this.units.get(String(id ?? ''))?.z ?? 0,
      other_name: (id: unknown) => this.units.get(String(id ?? ''))?.name() ?? '',
      goto: (x: unknown, z: unknown, s?: unknown) => current()?.goto(x, z, s) ?? false,
      stop: U((u) => u.stop(), false),
      moving: U((u) => u.moving(), false),
      home: U((u) => u.home(), false),
      wander: U1((u, a) => u.wander(a), false),
      teleport: U2((u, a, b) => u.teleport(a, b), false),
      face: U2((u, a, b) => u.face(a, b), false),
      face_player: U((u) => u.facePlayer(), false),
      set_speed: U1((u, a) => u.setSpeed(a), false),
      speed: U((u) => u.speed(), 0),
      path_to: (x: unknown, z: unknown, s?: unknown) => current()?.goto(x, z, s) ?? false,
      set_pathing: U1((u, a) => u.setPathing(a), false),
      follow_path: U1((u, a) => u.followPath(a), false),
      following: U((u) => u.following(), ''),
      stop_path: U((u) => u.stop(), false),
      dialogue: U1((u, a) => u.loadDialogue(a), false),
      dialogue_start: U((u) => u.startDialogue(), false),
      dialogue_clear: U((u) => u.clearDialogue(), false),
      schedule: U1((u, a) => u.loadSchedule(a), false),
      schedule_clear: U((u) => u.clearSchedule(), false),
      emote: U1((u, a) => u.emote(a), false),
      emoting: U((u) => u.emoting(), false),
      hide: U((u) => u.hide(), false), show: U((u) => u.show(), false), visible: U((u) => u.visible(), false),
      set_scale: U1((u, a) => u.setScale(a), false),
      tint: U1((u, a) => u.tint(a), false), untint: U((u) => u.untint(), false),
      player_name: () => atlas.username ?? '',
      player_x: () => atlas.localX, player_y: () => atlas.localY, player_z: () => atlas.localZ,
      player_dist: U((u) => u.playerDist(), 0),
      player_near: U1((u, r) => u.playerDist() <= (Number(r) || 0), false),
      player_facing_me: U1((u, deg) => this.playerFacing(u.x, u.z, deg === undefined || deg === null ? 30 : Number(deg) || 30), false),
      player_moving: () => this.playerSpeedEma > 0.3,
      player_speed: () => Math.round(this.playerSpeedEma * 100) / 100,
      player_mode: () => this.playerMode(),
      player_is_owner: () => this.playerIsOwner(),
      player_met: U((u) => this.playerMet(u), false),
      players_near: U1((u, r) => this.playersNear(u.x, u.z, Number(r) || 0), 0),
      set_sense: U1((u, r) => { u.senseR = Math.min(80, Math.max(1, Number(r) || DEFAULT_SENSE_M)); return true; }, false),
      sense: U((u) => u.senseR, 0),
      say: (t: unknown, s?: unknown) => current()?.say(t, s) ?? false,
      set_greeting: U1((u, a) => u.setGreeting(a), false),
      greeting: U((u) => u.greetingText, ''),
      add_topic: (k: unknown, l: unknown, r?: unknown) => current()?.addTopic(k, l, r) ?? false,
      remove_topic: U1((u, a) => u.removeTopic(a), false),
      clear_topics: U((u) => u.clearTopics(), false),
      topic_count: U((u) => u.topics.length, 0),
      ask: (q: unknown, ...o: unknown[]) => current()?.ask(q, ...o) ?? false,
      set_talkable: U1((u, a) => u.setTalkable(a), false),
      set_prompt: U1((u, a) => u.setPrompt(a), false),
      talking: U((u) => u.talking(), false),
      talking_to: U((u) => u.talkingTo(), ''),
      open_talk: U((u) => u.openTalk(), false),
      end_talk: U((u) => u.endTalk(), false),
      ground_y: (x: unknown, z: unknown) => { const c = atlas.conn; return c ? groundYIn(c, atlas.currentWorldId, Number(x) || 0, Number(z) || 0) : 0; },
      time_of_day: () => this.timeOfDay(),
      hour: U((u) => u.hour(), 12),
      is_night: U((u) => u.isNight(), false),
      weather: () => this.weather(),
      biome: U((u) => this.biomeUnder(u.x, u.z), ''),
      slope: U((u) => u.slope(), 0),
      in_water: U((u) => this.inWater(u.x, u.z), false),
      world_name: () => this.worldName(),
      world_size: () => this.worldSize(),
      is_commons: () => this.isCommons(),
      elapsed: U((u) => u.elapsed(), 0),
      part_near: U1((u, r) => { const rr = Number(r) || 0; return this.parts().filter((p) => Math.hypot(p.x - u.x, p.z - u.z) <= rr).length; }, 0),
      nearest_part: U((u) => { let b = ''; let bd = Infinity; for (const p of this.parts()) { const d = Math.hypot(p.x - u.x, p.z - u.z); if (d < bd) { bd = d; b = p.id; } } return b; }, ''),
      nearest_part_dist: U((u) => { let bd = -1; for (const p of this.parts()) { const d = Math.hypot(p.x - u.x, p.z - u.z); if (bd < 0 || d < bd) bd = d; } return bd; }, -1),
      player_count: () => this.playerCount(),
      signal: (t: unknown, p: unknown) => signal(t, p),
      timer: U2((u, a, b) => u.timer(a, b), false),
      chance: U1((u, a) => u.chance(a), false),
      random: U2((u, a, b) => u.random(a, b), 0),
      war_active: () => this.warActive(),
      nearest_town: U((u) => this.nearestTown(u.x, u.z)?.name ?? '', ''),
      nearest_town_dist: U((u) => this.nearestTown(u.x, u.z)?.dist ?? -1, -1),
      versona_count: () => this.versonas().length,
      versona_id: (i: unknown) => this.versonas()[Number(i) - 1]?.id ?? '',
      nearest_versona: U((u) => { let b = ''; let bd = Infinity; for (const v of this.versonas()) { const d = Math.hypot(v.x - u.x, v.z - u.z); if (d < bd) { bd = d; b = v.id; } } return b; }, ''),
      versona_name: (id: unknown) => vOf(id)?.name ?? '',
      versona_x: (id: unknown) => vOf(id)?.x ?? 0,
      versona_z: (id: unknown) => vOf(id)?.z ?? 0,
      versona_dist: U1((u, id) => { const v = vOf(id); return v ? Math.hypot(v.x - u.x, v.z - u.z) : -1; }, -1),
      versona_state: (id: unknown) => vOf(id)?.state ?? '',
      versona_job: (id: unknown) => vOf(id)?.job ?? '',
      versona_mood: (id: unknown) => vOf(id)?.mood ?? 50,
      goto_versona: U1((u, id) => u.gotoVersona(id), false),
      versona_ask: U2((u, id, t) => u.versonaAsk(id, t), false),
      versona_last: U1((u, id) => u.versonaLast.get(String(id ?? '')) ?? '', ''),
    };
    // ONE-catalog law: the bridge and lua_api.ts must agree (dev-time check).
    const catalogued = new Set([...knownCalls('worldscript')].filter((n) => n.startsWith('npc.')).map((n) => n.slice(4)));
    const built = new Set(Object.keys(api));
    const missing = [...catalogued].filter((n) => !built.has(n));
    const extra = [...built].filter((n) => !catalogued.has(n));
    if (missing.length || extra.length) console.warn('[npc-api] catalog/bridge drift', { missing, extra });
    return api;
  }

  dispose(): void {
    this.teardown();
    this.scene.remove(this.root, this.versonaMarkerRoot);
    this.win?.root.remove();
  }
}
